import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toggleConversationFavorite } from './api';
import { QUERY_KEYS } from '@src/query';
import { ApiError } from '@src/services/api';

export const useMutationToggleConversationFavorite = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    { status: string },
    ApiError,
    {
      networkName: string;
      networkSendersId: string;
      networkReceiverId: string;
      isFavorite: boolean;
    }
  >({
    mutationFn: toggleConversationFavorite,
    // Optimistic update: immediately reflect the new favorite status in the cached stored conversations
    onMutate: async (variables) => {
      const keyStored = QUERY_KEYS.conversations();
      await queryClient.cancelQueries({ queryKey: keyStored });

      const previousStored: any = queryClient.getQueryData(keyStored);

      queryClient.setQueryData(keyStored, (old: any) => {
        if (!old || !Array.isArray(old.pages)) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            conversations: (page.conversations ?? []).map((c: any) =>
              c.targetAccountId === variables.networkReceiverId && c.interlocutorId === variables.networkSendersId
                ? { ...c, isFavorite: variables.isFavorite }
                : c,
            ),
          })),
        };
      });

      return { previousStored };
    },

    onError: (_err, _variables, context: any) => {
      const keyStored = QUERY_KEYS.conversations();
      if (context?.previousStored) queryClient.setQueryData(keyStored, context.previousStored);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.remoteConversations() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conversations() });
    },
  // onSuccess handled by onSettled
  });
};
